import { useEffect, useMemo, useState } from "react";
import { Box, Button, Stack, Switch, Typography } from "@mui/material";
import { Link as RouterLink, Navigate } from "react-router-dom";
import { useAuth } from "../context/auth";
import { useMyDeities } from "../features/myshrine/useMyDeities";
import GenkanBackground from "../features/home/approaches";
import KiokuSanpai from "../features/en/KiokuSanpai";
import useShrineNames from "../features/en/useShrineNames";
import { useDrops, useEnConfig, useEnSettings, useMyVisits, useSharedCount } from "../features/en/useEn";
import TodayDrop from "../features/en/TodayDrop";

const SERIF = '"Hiragino Mincho ProN", "Yu Mincho", serif';
const WASHI = "#ece5d4";
const WASHI_DIM = "rgba(222,215,198,0.55)";
const WASHI_FAINT = "rgba(222,215,198,0.4)";
const GOLD = "#d4b46e";
const GOLD_TEXT = "#e6d3a3";

const QUIET_SX = {
  color: WASHI_DIM,
  fontSize: "0.78rem",
  fontFamily: SERIF,
  letterSpacing: 2,
  "&:hover": { color: WASHI, bgcolor: "rgba(255,255,255,0.04)" },
};

function GoldRule({ mb = 4 }) {
  return (
    <Box
      sx={{
        width: 52,
        height: "1px",
        mx: "auto",
        mb,
        background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
      }}
    />
  );
}

/** 一文（見出しの一行）。静かに立ち上がる */
function Line({ children, sub, entered = true, mb = 4 }) {
  return (
    <Box
      sx={{
        textAlign: "center",
        mb,
        opacity: entered ? 1 : 0,
        transform: entered ? "none" : "translateY(14px)",
        transition: "opacity 900ms ease, transform 900ms cubic-bezier(0.2,0.7,0.2,1)",
      }}
    >
      <Typography
        sx={{
          fontFamily: SERIF,
          fontSize: { xs: "1.05rem", sm: "1.2rem" },
          letterSpacing: 2,
          lineHeight: 2.1,
          color: WASHI,
          whiteSpace: "pre-line",
        }}
      >
        {children}
      </Typography>
      {sub && (
        <Typography
          sx={{
            fontFamily: SERIF,
            fontSize: "0.8rem",
            letterSpacing: 1.5,
            lineHeight: 2.2,
            color: WASHI_DIM,
            mt: 1,
            whiteSpace: "pre-line",
          }}
        >
          {sub}
        </Typography>
      )}
    </Box>
  );
}

/** 安心の一行。常に足元に置く（安全の三原則を UI に敷く） */
function Anshin() {
  return (
    <Typography
      sx={{
        fontFamily: SERIF,
        textAlign: "center",
        fontSize: "0.7rem",
        letterSpacing: 1.5,
        lineHeight: 2,
        color: "rgba(222,215,198,0.38)",
        mt: 6,
      }}
    >
      経路は記録しません。今いる人は出しません。
    </Typography>
  );
}

const DOORS = [
  { label: "社", sub: "縁を結んだ神様が、ここに降りてくる", to: "/keidai" },
  { label: "近くの神社", sub: "新しいご縁を結びに行く", to: "/nearby" },
  { label: "参拝ログ", sub: "参った社が、積もっていく", to: "/me/history" },
];

function Doors() {
  return (
    <Stack spacing={1.5}>
      {DOORS.map((d) => (
        <Box
          key={d.to}
          component={RouterLink}
          to={d.to}
          sx={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 2,
            px: 3,
            py: 2,
            textDecoration: "none",
            border: "1px solid rgba(212,180,110,0.35)",
            bgcolor: "rgba(212,180,110,0.04)",
            transition: "all 0.3s ease",
            "&:hover": {
              bgcolor: "rgba(212,180,110,0.1)",
              borderColor: "rgba(224,201,143,0.8)",
              boxShadow: "0 0 28px rgba(212,180,110,0.16)",
            },
          }}
        >
          <Typography
            sx={{ fontFamily: SERIF, fontWeight: 600, fontSize: "1.05rem", letterSpacing: 4, color: GOLD_TEXT, whiteSpace: "nowrap" }}
          >
            {d.label}
          </Typography>
          <Typography sx={{ fontFamily: SERIF, fontSize: "0.78rem", letterSpacing: 1, color: WASHI_DIM, textAlign: "right" }}>
            {d.sub}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

/**
 * 「あなたと同じ神社に参った人が、もう N 人います。」
 * N は countSharedVisitors の実数（自分を除く・縁を開いた人だけ・名前は返さない）。
 * 取れないとき（未デプロイ・回数上限・停止中）は数字を出さず、静かに消える。
 */
function SharedCount({ shared, sodeOpen, onToggle }) {
  const total = shared?.data?.enabled ? shared.data.total : null;
  if (typeof total !== "number") return null;
  // 有名神社で索引を読み切れないとき、総数は下限値になる（Functions が approx:true を返す）
  const approx = shared.data.approx === true;
  return (
    <Box sx={{ textAlign: "center", mb: 5 }}>
      <Typography sx={{ fontFamily: SERIF, fontSize: "0.85rem", letterSpacing: 1.5, color: WASHI_DIM, lineHeight: 2 }}>
        あなたと同じ神社に参った人が、
      </Typography>
      <Typography component="p" sx={{ fontFamily: SERIF, color: WASHI, letterSpacing: 2, lineHeight: 1.6, mt: 0.5 }} data-testid="en-count">
        <Box component="span" sx={{ fontSize: "0.9rem", color: WASHI_DIM, mr: 1 }}>
          もう
        </Box>
        <Box
          component="span"
          sx={{
            fontSize: { xs: "2.4rem", sm: "2.8rem" },
            color: GOLD_TEXT,
            textShadow: "0 0 24px rgba(212,180,110,0.35)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {total.toLocaleString("ja-JP")}
        </Box>
        <Box component="span" sx={{ fontSize: "0.9rem", color: WASHI_DIM, ml: 1 }}>
          {approx ? "人以上います。" : "人います。"}
        </Box>
      </Typography>
      <Typography sx={{ fontFamily: SERIF, fontSize: "0.72rem", letterSpacing: 1.5, color: WASHI_FAINT, mt: 1 }}>
        顔も名前も、出しません。
      </Typography>

      {/* 縁を開く（sodeOpen）。既定は閉じている。開いた人だけが、相手の数に入り、引き合わせの候補になる */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={1}
        sx={{ mt: 2.5, px: 1.5, py: 0.5 }}
      >
        <Typography sx={{ fontFamily: SERIF, fontSize: "0.78rem", letterSpacing: 1.2, color: sodeOpen ? WASHI : WASHI_DIM }}>
          {sodeOpen ? "あなたの縁も、開いています。" : "あなたの縁は、まだ閉じています。"}
        </Typography>
        <Switch
          size="small"
          checked={!!sodeOpen}
          onChange={(e) => onToggle?.(e.target.checked)}
          slotProps={{ input: { "aria-label": "縁を開く" } }}
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": { color: GOLD },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: GOLD },
            "& .MuiSwitch-track": { bgcolor: "rgba(222,215,198,0.4)" },
          }}
        />
      </Stack>
      <Typography sx={{ fontFamily: SERIF, fontSize: "0.68rem", letterSpacing: 1, color: WASHI_FAINT, lineHeight: 1.9 }}>
        {sodeOpen
          ? "同じ神社に参った人の数に、あなたも入ります。いつでも閉じられます。"
          : "開くと、同じ神社に参った人の数にあなたも入り、御祭神の引き合わせを待てます。"}
      </Typography>
    </Box>
  );
}

/**
 * /top ＝ ログイン後のトップ（2026-10-13 新設、10-14 作り込み）。
 *
 * 骨 v3 の決定 #8「入口は記憶の参拝」をここに置く。三つの状態がある。
 *   1. 初回（記憶の参拝も参拝ログも無い）: 記憶の参拝そのものが画面。
 *   2. 記した直後: 「あなたと同じ神社に参った人が、もう N 人います。」が灯る。
 *   3. 二回目以降: 今日の一行（縁が降りた日は御祭神の引き合わせ／ふだんは今日の神様）＋ N 人 ＋ 三つの扉。
 *
 * 裏側（Phase A）: visits は users/{uid}/visits に本人 create のみ（rules で形を固定）。
 * N は Functions の countSharedVisitors。config/en.enabled=false（キルスイッチ）のときは
 * 記憶の参拝も N も出さず、従来どおり一行と扉だけになる。
 * 縁が降りる（todayDrop）は Phase B: useDrops().today を TodayDrop 部品で出す（縁を結ぶ／見送る）。
 * 人と人の縁については「縁」「引き合わせ」「御祭神」でしか語らない（出会い・マッチング・袖振り合うは出さない）。
 */
export default function TopPage() {
  const { user, loading } = useAuth();
  const { deities, loading: deitiesLoading } = useMyDeities();
  const { allowed: enAllowed } = useEnConfig();
  const { visits, loaded: visitsLoaded, addMemory } = useMyVisits();
  const { sodeOpen, setSodeOpen } = useEnSettings();

  // 記した社の id（重複を除く）。名前は visits に無いので shrines から補う（検索結果の分は手元を使う）
  const shrineIds = useMemo(() => Array.from(new Set(visits.map((v) => v.shrineId).filter(Boolean))), [visits]);
  const [known, setKnown] = useState({});
  const names = useShrineNames(shrineIds, known);
  const recorded = useMemo(
    () => shrineIds.map((id) => names[id] || { shrineId: id, name: "", prefecture: "", city: "" }),
    [shrineIds, names]
  );

  const shared = useSharedCount(enAllowed && shrineIds.length > 0, [shrineIds.length]);

  // 記憶の参拝を開いているか。初回は自動で開く。二回目以降は「思い出す」で開く
  const [opened, setOpened] = useState(null); // null = まだ決めていない（初回判定待ち）
  const [saving, setSaving] = useState(false);

  const firstTime = enAllowed && visitsLoaded && !deitiesLoading && deities.length === 0 && shrineIds.length === 0;
  const showKioku = enAllowed && (opened === null ? firstTime : opened);

  // 入場（静かにフェード）
  const [entered, setEntered] = useState(() => {
    if (typeof window === "undefined") return true;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return true;
    return false;
  });
  useEffect(() => {
    if (entered) return undefined;
    const t = window.setTimeout(() => setEntered(true), 40);
    return () => window.clearTimeout(t);
  }, [entered]);

  // 日替わりで「今日待っている神様」を決める（ご縁のある神様の中から）
  const deityOfTheDay = useMemo(() => {
    if (!deities.length) return null;
    const dayIndex = Math.floor(Date.now() / 86400000);
    return deities[dayIndex % deities.length];
  }, [deities]);

  // 今日降りた縁（Phase B）。縁の機能が止まっているときは出さない
  const { today: rawDrop, wave, pass } = useDrops();
  const todayDrop = enAllowed ? rawDrop : null;
  // 一行に出す社＝重なった社の先頭（珍しい順なので一番濃い社）
  const dropNames = useShrineNames(todayDrop?.shrineIds || [], {});
  const dropShrineName = todayDrop ? dropNames[todayDrop.shrineIds?.[0]]?.name || "縁の社" : "";

  if (!loading && !user) return <Navigate to="/" replace />;

  const handleDone = async (list) => {
    setSaving(true);
    // 名前は手元に残す（visits には無い）
    setKnown((prev) => ({ ...prev, ...Object.fromEntries(list.map((s) => [s.shrineId, s])) }));
    for (const s of list) {
      if (shrineIds.includes(s.shrineId)) continue;
      try {
        await addMemory(s.shrineId);
      } catch (e) {
        // 1 日の上限・形の不一致などは静かに飛ばす（rules / Functions が最終判定）
      }
    }
    setSaving(false);
    setOpened(false);
  };

  // ── 一行の決め方 ──
  let line;
  let sub;
  if (todayDrop) {
    line = `${dropShrineName}の御祭神が、\nひとつの縁を引き合わせました。`;
    sub = null;
  } else if (deityOfTheDay) {
    line = `${deityOfTheDay.name}が、社で待っています。`;
  } else if (shrineIds.length > 0) {
    line = "参った社の縁は、\nもう結ばれています。";
    sub = "次は、歩いて参りに。";
  } else {
    line = "最初のご縁を、結びに行きましょう。";
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "calc(100dvh - 180px)" }}>
      <GenkanBackground />

      <Box
        sx={{
          maxWidth: 480,
          width: "100%",
          mx: "auto",
          px: 2.5,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          pt: { xs: 4, sm: 6 },
          pb: 8,
        }}
      >
        {showKioku ? (
          /* 1. 記憶の参拝 */
          <Box sx={{ opacity: entered ? 1 : 0, transition: "opacity 900ms ease" }}>
            <KiokuSanpai
              fixed={recorded}
              compact={!firstTime}
              busy={saving}
              onDone={handleDone}
              onCancel={() => setOpened(false)}
            />
            <Anshin />
          </Box>
        ) : (
          <>
            {/* 2 / 3. 今日の一行 */}
            <Line entered={entered} sub={sub}>
              {line}
            </Line>

            <GoldRule />

            {/* 縁が降りた日（Phase B）。相手の名前・顔・日付は出さない */}
            {todayDrop && <TodayDrop drop={todayDrop} onWave={wave} onPass={pass} />}

            {/* 同じ神社に参った人（骨 v3 Phase A の完了条件） */}
            {enAllowed && shrineIds.length > 0 && (
              <SharedCount shared={shared} sodeOpen={sodeOpen} onToggle={(v) => setSodeOpen(v).catch(() => {})} />
            )}

            <Doors />

            {enAllowed && (
              <Stack alignItems="center" sx={{ mt: 3 }}>
                <Button variant="text" size="small" sx={QUIET_SX} onClick={() => setOpened(true)}>
                  {shrineIds.length > 0 ? "参った社を、もうひとつ思い出す" : "これまでに参った神社を、思い出す"}
                </Button>
              </Stack>
            )}

            <Anshin />
          </>
        )}
      </Box>
    </Box>
  );
}
